import { SkyButton, SkyCard, SkyDrawer, SkySwitch, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import {
  SECURITY_CATEGORIES,
  SECURITY_CATEGORY_DESCRIPTIONS,
  SECURITY_CATEGORY_LABELS,
  SECURITY_PREFS_SAVED_MESSAGE,
  isAlwaysSentSecurityCategory,
  type SecurityCategory,
  type SecurityPrefsView,
} from "@shared/types/securityAlerts";
import {
  useCanSubscribeToSecurityAlerts,
  useSaveSecurityPrefs,
  useSecurityPrefs,
} from "@/hooks/use-security-alerts";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { byCodeUnit } from "@shared/sort";

/**
 * Security alerts in My alerts (docs/research/2026-09-28-design-security-alerts.md, "UI"): a
 * TenantAdmin or PlatformAdmin chooses which security acts email them. The user menu shows how
 * many are on and a button; the choice itself is a drawer (an edit that keeps context), rendered
 * outside the menu so closing the menu does not close it. Break-glass and repeated failed
 * sign-ins are on until switched off (owner answer W3-14); the other four are opt-in.
 */

/** "For Contoso and its instances.", "For Contoso and Fabrikam and their instances." */
export function scopeSentence(scope: SecurityPrefsView["scope"]): string {
  if (scope.deployment) return "For the whole deployment.";
  const names = scope.tenantNames;
  if (names.length === 0) return "For the tenants you administer.";
  if (names.length === 1) return `For ${names[0]} and its instances.`;
  return `For ${names.slice(0, -1).join(", ")} and ${names.at(-1)} and their instances.`;
}

/** How many categories reach the caller: their choice, plus the always-sent ones for a PlatformAdmin. */
function receivedCount(view: SecurityPrefsView): number {
  return SECURITY_CATEGORIES.filter(
    (category) =>
      view.categories.includes(category) ||
      (view.scope.deployment && isAlwaysSentSecurityCategory(category)),
  ).length;
}

/** "3 of 7 on", or why the count is not there yet. */
function receivedLine(view: SecurityPrefsView | undefined, isError: boolean): string {
  if (view) return `${receivedCount(view)} of ${SECURITY_CATEGORIES.length} on`;
  return isError ? "Your security alerts did not load." : "Loading…";
}

/** Inside the user menu: nothing for anyone who cannot subscribe. */
export function SecurityAlertsEntry({ onChoose }: { onChoose: () => void }) {
  const allowed = useCanSubscribeToSecurityAlerts();
  const prefs = useSecurityPrefs(allowed);
  if (!allowed) return null;
  const view = prefs.data;
  return (
    <div className="flex flex-col gap-2 px-4 py-2" data-testid="security-alerts-entry">
      <SkyText as="p" size={200} color="muted" className="uppercase tracking-wide">
        Security alerts
      </SkyText>
      <div className="flex items-center gap-2">
        <SkyText size={200} className="min-w-0 flex-1">
          {receivedLine(view, prefs.isError)}
        </SkyText>
        <SkyButton
          appearance="secondary"
          size="small"
          testId="security-alerts-choose"
          aria-label="Choose security alerts"
          onClick={onChoose}
        >
          Choose
        </SkyButton>
      </div>
    </div>
  );
}

/** The drawer: one switch per category, its one line, where the alerts apply, and Save. */
export function SecurityAlertsDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const allowed = useCanSubscribeToSecurityAlerts();
  const prefs = useSecurityPrefs(allowed && open);
  const save = useSaveSecurityPrefs();
  const saveError = useInlineError();
  const [draft, setDraft] = useState<SecurityCategory[] | null>(null);
  const [saved, setSaved] = useState(false);
  const view = prefs.data;

  // A drawer opened again starts from what is stored: closing it drops the draft.
  function close() {
    setDraft(null);
    setSaved(false);
    saveError.clear();
    onClose();
  }

  if (!allowed) return null;
  const chosen = draft ?? view?.categories ?? [];
  const dirty =
    draft !== null &&
    [...draft].sort(byCodeUnit).join(",") !==
      [...(view?.categories ?? [])].sort(byCodeUnit).join(",");

  function toggle(category: SecurityCategory, on: boolean) {
    setSaved(false);
    const next = new Set(chosen);
    if (on) next.add(category);
    else next.delete(category);
    setDraft(SECURITY_CATEGORIES.filter((item) => next.has(item)));
  }

  function handleSave() {
    saveError.clear();
    save.mutate(
      { categories: chosen },
      {
        onSuccess: () => {
          setDraft(null);
          setSaved(true);
        },
        onError: (error) => saveError.show(error, "Your security alerts could not be saved."),
      },
    );
  }

  return (
    <SkyDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      position="end"
      size="medium"
      headerTitle="Security alerts"
      showCloseButton
      testId="security-alerts-drawer"
    >
      <SkyCard className="flex flex-col gap-4 p-4">
        {!view ? (
          <SkyText size={200} color="muted">
            {prefs.isError ? "Your security alerts did not load. Close and try again." : "Loading…"}
          </SkyText>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <SkyText size={300} block>
                {scopeSentence(view.scope)}
              </SkyText>
              <SkyText size={200} color="muted" block>
                {view.callerEmailMasked
                  ? `Sent to ${view.callerEmailMasked}. Times in the emails use the time zone you choose under Time zone, or Pacific time until you choose one.`
                  : "Your account has no email address, so there is nowhere to send them."}
              </SkyText>
              {/* #1425: the runner leaves the person who acted out of every category but break-glass. */}
              <SkyText size={200} color="muted" block testId="security-alerts-own-changes">
                You are not alerted about a change you make yourself, except self-granted access
                (break-glass).
              </SkyText>
              {saved && !dirty ? (
                <div role="status">
                  <SkyText size={200} weight="semibold" block>
                    {SECURITY_PREFS_SAVED_MESSAGE}
                  </SkyText>
                </div>
              ) : null}
            </div>
            <ul className="flex flex-col gap-4" aria-label="Security alert categories">
              {SECURITY_CATEGORIES.map((category) => {
                // Wave 4 answer Q2: a platform admin always receives an always-sent category
                // (audit integrity), so the switch shows it on and cannot turn it off.
                const always = view.scope.deployment && isAlwaysSentSecurityCategory(category);
                return (
                  <li key={category}>
                    <SkySwitch
                      label={SECURITY_CATEGORY_LABELS[category]}
                      description={
                        always
                          ? "Always on for platform admins: an audit trail failed its daily integrity check, or its write-once export or retention failed."
                          : SECURITY_CATEGORY_DESCRIPTIONS[category]
                      }
                      checked={always || chosen.includes(category)}
                      onCheckedChange={(on) => toggle(category, on)}
                      disabled={always || save.isPending}
                      testId={`security-alert-${category}`}
                    />
                  </li>
                );
              })}
            </ul>
            {saveError.error ? (
              <InlineErrorBar
                error={saveError.error}
                title="Your security alerts could not be saved"
                onDismiss={saveError.clear}
                testId="security-alerts-save-error"
              />
            ) : null}
            <div>
              <SkyButton
                appearance="primary"
                testId="security-alerts-save"
                disabled={!dirty || save.isPending}
                onClick={handleSave}
              >
                Save security alerts
              </SkyButton>
            </div>
          </>
        )}
      </SkyCard>
    </SkyDrawer>
  );
}
